/**
 * @file index.test.tsx
 *
 * @fileoverview The JobDetail component.
 */

import { cva, VariantProps } from "class-variance-authority";
import { Fragment, ReactNode, useRef, useState } from "react";
import { useIntersectionObserver } from "../../../../hooks/useIntersectionObserver";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import BadgeIcon from "@jq-icons/Badge";
import CalendarIcon from "@jq-icons/Calendar";
import CloseIcon from "@jq-icons/Close";
import HomeIcon from "@jq-icons/Home";
import MapMarkerIcon from "@jq-icons/MapMarker";
import OfficeIcon from "@jq-icons/Office";
import { JobDate } from "../../../../models/job";
import Badge from "../../../02-atoms/03-badge";
import HideDesktop from "../../../02-atoms/06-layout/02-visibility/01-hide-desktop";
import Spacing from "../../../02-atoms/06-layout/07-spacing";
import PopoverList from "../../../03-molecules/13-popover-list";
import styles from "./jobDetail.module.scss";
import HiddenVisually from "../../../02-atoms/14-w-a11y/02-visually-hidden";
import TrophyIcon from "@jq-icons/Trophy";
import SourcePortals from "../../../03-molecules/16-source-portals";

const jobDetailWrapper = cva([styles.jobDetailWrapper], {
  variants: {
    fullContainer: {
      true: styles.jobDetailWrapperFullContainer,
    },
    overflow: {
      true: styles.jobDetailWrapperOverflow,
    },
    isIframe: {
      true: styles.jobDetailWrapperIsIframe,
    },
    withBottomMenu: {
      true: styles.jobDetailWrapperWithBottomMenu,
    },
  },
  defaultVariants: {
    fullContainer: false,
    overflow: false,
    isIframe: false,
    withBottomMenu: false,
  },
});
const jobDetailInfo = cva([styles.jobDetailInfo], {
  variants: {
    hasBanner: {
      true: styles.jobDetailInfoHasBanner,
    },
    verticalLayoutTablet: {
      true: styles.jobDetailInfoVerticalLayoutTablet,
    },
  },
  defaultVariants: {
    verticalLayoutTablet: false,
  },
});

export interface JobDetailProps
  extends VariantProps<typeof jobDetailWrapper>,
    VariantProps<typeof jobDetailInfo> {
  /**
   * The logo for the job detail.
   */
  picture?: {
    url: string;
    title: string;
    alt: string;
  };
  /**
   * Title for the job.
   */
  title: string;
  /**
   * Company for the job (could be a link).
   */
  company?: ReactNode;
  /**
   * The locations array for this job.
   */
  locations: ReactNode[];
  /**
   * Formatted job fields values.
   *
   * @default []
   */
  jobFieldsValues?: Array<Array<ReactNode | undefined>>;
  /**
   * The element to use as the title
   *
   * @default "h1"
   */
  titleElement?: string;
  /**
   * Extra elements that can be added bellow company and tags.
   */
  jobHeaderExtraInfo?: ReactNode;
  /**
   * Children should be the description for the job.
   */
  children: ReactNode;
  /**
   * Action buttons for the job.
   */
  actions?: ReactNode;
  /**
   * Edit link for the job.
   */
  edit?: ReactNode;
  /**
   * If the job is featured.
   */
  featured?: boolean;
  /**
   * If the job is featured.
   */
  featuredtext?: ReactNode;
  /**
   * The featured job icon.
   */
  featuredJobIcon?: ReactNode;
  /**
   * Signifies if the job is marked as top job.
   */
  topJob?: boolean;
  /**
   * The top job text.
   */
  topJobText?: ReactNode;
  /**
   * The icon for the top job.
   */
  topJobIcon?: ReactNode;
  /**
   * A callback for if the back button should display in a shrinked header.
   */
  onShrinkHeaderBack?: () => void;
  /**
   * The job dates.
   */
  dates?: JobDate[];
  /**
   * IF the job is remote the remote options for the job.
   */
  remote?: string | ReactNode;
  /**
   * The bookmark component.
   *
   * @default null
   */
  Bookmark?: ReactNode;
  /**
   * The banner image element.
   *
   * @default null
   */
  Banner?: ReactNode;
  /**
   * The premium company flag.
   *
   * @default false
   */
  premiumCompany?: boolean;
  /**
   * The optional custom badge component.
   */
  customBadge?: ReactNode;
  /**
   * The source portals for the job.
   */
  sourcePortals?: string[];
  /**
   * The organization icon for the job.
   */
  organizationIcon?: ReactNode;
  /**
   * The promoted organization icon for the job.
   */
  promotedOrganizationIcon?: ReactNode;
}

/**
 * The job detail presents information about a job when
 * the user clicks on a job in the search to visit it.
 */
export default function JobDetail({
  picture,
  title,
  company,
  locations,
  jobFieldsValues = [],
  children,
  jobHeaderExtraInfo,
  edit,
  actions,
  titleElement = "h1",
  fullContainer = false,
  featured = false,
  featuredtext,
  featuredJobIcon,
  topJob = false,
  topJobText,
  topJobIcon,
  isIframe = false,
  onShrinkHeaderBack,
  overflow = false,
  dates,
  remote,
  Bookmark = null,
  Banner = null,
  verticalLayoutTablet = false,
  premiumCompany = false,
  withBottomMenu = false,
  customBadge,
  sourcePortals,
  organizationIcon,
  promotedOrganizationIcon,
}: JobDetailProps) {
  const root = useRef(null);
  const target = useRef<HTMLDivElement>(null);
  const [isInfoVisible, setInfoVisible] = useState(true);

  const endElementTarget = useRef<HTMLDivElement>(null);
  const [isEndVisible, setIsEndVisible] = useState(false);

  const isMobile = useIsMobile();

  useIntersectionObserver({
    root,
    target,
    threshold: 0,
    onIntersect: ([{ isIntersecting }]) => setInfoVisible(isIntersecting),
  });

  useIntersectionObserver({
    root: root,
    target: endElementTarget,
    threshold: 0,
    rootMargin: "100px 0px 100px 0px",
    onIntersect: ([{ isIntersecting }]) => setIsEndVisible(isIntersecting),
  });

  const TopJobIconElement = topJobIcon ?? <TrophyIcon />;
  const FeaturedJobIconElement = featuredJobIcon ?? <BadgeIcon />;
  const OrganizationIconElement = organizationIcon ?? (
    <OfficeIcon color="secondary" title="company" />
  );
  const PromotedOrganizationIconElement = promotedOrganizationIcon ?? (
    <BadgeIcon color="featured" />
  );
  const CompanyIcon = premiumCompany
    ? PromotedOrganizationIconElement
    : OrganizationIconElement;

  const TitleElement: any = titleElement;
  const popoverPosition = picture || isMobile ? "bottom" : "right";

  return (
    <div
      className={jobDetailWrapper({
        fullContainer,
        isIframe,
        overflow,
        withBottomMenu,
      })}
      data-testid="job-detail-wrapper"
    >
      <div className={styles.jobBanner}>{Banner}</div>
      <div
        className={jobDetailInfo({
          verticalLayoutTablet: verticalLayoutTablet,
          hasBanner: !!Banner,
        })}
        ref={target}
        data-testid="job-detail-info"
      >
        {picture && (
          <div className={styles.jobPictureWrapper}>
            <img
              className={styles.jobLogo}
              src={picture.url}
              alt={picture.title}
              title={picture.title}
              data-testid="job-logo"
            />
          </div>
        )}
        <div className={styles.jobMainInfoWrapper}>
          <div className={styles.jobCardTitleWrapper}>
            <TitleElement>
              <span className={styles.jobCardTitle} data-testid="title">
                {title}
              </span>
            </TitleElement>
            {topJob && topJobText ? (
              <Badge badgeStyle="topJob">
                {TopJobIconElement}
                <span>{topJobText}</span>
              </Badge>
            ) : (
              featured &&
              featuredtext && (
                <Badge badgeStyle="featured">
                  {FeaturedJobIconElement}
                  <span>{featuredtext}</span>
                </Badge>
              )
            )}
            {customBadge}
            {Bookmark}
          </div>
          <div className={styles.jobInfo}>
            {company && (
              <div className={styles.jobInfoItem} data-testid="company-name">
                <span className={styles.jobIcon}>{CompanyIcon}</span>
                {company}
              </div>
            )}
            {locations && (
              <div className={styles.jobInfoItem}>
                <PopoverList
                  Icon={<MapMarkerIcon color="secondary" title="location" />}
                  items={locations}
                  position={popoverPosition}
                />
              </div>
            )}
            {remote && (
              <div className={styles.jobInfoItem}>
                <span className={styles.jobIcon}>
                  <HomeIcon color="secondary" title="remote" />
                </span>
                {remote}
              </div>
            )}
            {dates?.map((date, index) => (
              <div className={styles.jobInfoItem} key={index}>
                <span className={styles.jobIcon}>
                  {date.icon ? (
                    date.icon
                  ) : (
                    <CalendarIcon
                      color="secondary"
                      title={date.title ?? "Date"}
                    />
                  )}
                </span>
                <HiddenVisually>
                  {date.title}
                  {`:`}&nbsp;
                </HiddenVisually>
                {date.text}
              </div>
            ))}
          </div>
          {Boolean(jobFieldsValues?.length) && (
            <div
              className={styles.jobCardJobFields}
              data-testid="fields-values"
            >
              {jobFieldsValues?.map(
                (fieldValues: (ReactNode | undefined)[], index: number) =>
                  fieldValues ? (
                    <Fragment key={index}>
                      <PopoverList
                        items={fieldValues as ReactNode[]}
                        position={popoverPosition}
                      />
                      <span className={styles.jobFieldSeparator}>
                        {index + 1 !== jobFieldsValues.length && " | "}
                      </span>
                    </Fragment>
                  ) : null
              )}
            </div>
          )}
          {jobHeaderExtraInfo}
          {sourcePortals && sourcePortals.length > 0 && (
            <SourcePortals sourcePortals={sourcePortals} />
          )}
        </div>
        {edit && <div className={styles.jobEditWrapper}>{edit}</div>}
        {actions && <div className={styles.jobActions}>{actions}</div>}
      </div>
      {!isInfoVisible && (
        <div className={styles.jobShrinkedHeader}>
          {picture && (
            <div>
              <img
                className={styles.jobLogo}
                src={picture.url}
                alt={picture.title}
                title={picture.title}
              />
            </div>
          )}
          <div>
            <div className={styles.jobShrinkedCardTitle}>{title}</div>
            <div className={styles.jobShrinkedCardCompanyName}>
              <span className={styles.jobIcon}>{CompanyIcon}</span>
              {company}
            </div>
          </div>
          <div className={styles.flexSpace} />
          <div className={styles.shrinkedCardActionsCell}>{actions}</div>
          {onShrinkHeaderBack && overflow && (
            <HideDesktop>
              <button onClick={onShrinkHeaderBack}>
                <CloseIcon height="20" width="20" title="close" color="gray4" />
              </button>
            </HideDesktop>
          )}
        </div>
      )}
      {children && (
        <div
          className={styles.jobDetailDescription}
          data-testid="job-detail-description"
        >
          {children}
          <Spacing />
        </div>
      )}
      {!isInfoVisible && !isEndVisible && (
        <HideDesktop>
          <div className={styles.jobShrinkedHeaderActions}>{actions}</div>
        </HideDesktop>
      )}
      <div ref={endElementTarget} />
    </div>
  );
}
export { JobDetail };
